import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Megaphone, Save } from "lucide-react";
import { toast } from "react-toastify";
import API from "../../../util/API";
import AdminPageShell from "../components/AdminPageShell";

const cx = (...arr) => arr.filter(Boolean).join(" ");

const FieldBase =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30";

const Card = ({ children, className }) => (
  <section className={cx("bg-white border border-slate-200 rounded-2xl p-5", className)}>
    {children}
  </section>
);

const PrimaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors",
      className
    )}
  />
);

const DEFAULT_HTML = "Limited-time offer: <strong>20% off</strong> with code <strong>SAVE20</strong>";

export default function MarketingContentPage() {
  const [announcementHtml, setAnnouncementHtml] = useState("");
  const [initialHtml, setInitialHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await API.get("/settings/announcement-bar");
        const html = String(res?.data?.html || "");
        if (cancelled) return;
        setAnnouncementHtml(html);
        setInitialHtml(html);
        setUpdatedAt(res?.data?.updated_at || null);
      } catch (err) {
        if (cancelled) return;
        toast.error(err?.response?.data?.message || "Failed to load announcement bar setting.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const isDirty = useMemo(
    () => String(announcementHtml || "") !== String(initialHtml || ""),
    [announcementHtml, initialHtml]
  );

  const previewHtml = useMemo(
    () => String(announcementHtml || "").trim() || DEFAULT_HTML,
    [announcementHtml]
  );

  const onSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await API.put("/settings/announcement-bar", {
        html: String(announcementHtml || ""),
      });
      const nextHtml = String(res?.data?.html || "");
      setAnnouncementHtml(nextHtml);
      setInitialHtml(nextHtml);
      setUpdatedAt(res?.data?.updated_at || new Date().toISOString());
      toast.success("Announcement bar updated.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save announcement bar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminPageShell
      title="Marketing & Content"
      description="Manage storefront announcement bar content for cherrfy.com."
      chips={
        <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-black text-slate-800">
          <Megaphone className="h-4 w-4" />
          Announcement Bar
        </span>
      }
    >
      <div className="grid grid-cols-1 gap-4 md:gap-6">
        <Card>
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">
                Announcement Bar / Popup HTML
              </h2>
              <p className="mt-1 text-xs text-slate-500">
                This HTML is shown in the storefront header announcement bar.
              </p>
              {updatedAt ? (
                <p className="mt-2 text-xs text-slate-500">
                  Last updated: <span className="font-semibold text-slate-700">{new Date(updatedAt).toLocaleString()}</span>
                </p>
              ) : null}
            </div>
            <PrimaryButton
              type="button"
              onClick={onSave}
              disabled={saving || loading || !isDirty}
              className={cx((saving || loading || !isDirty) ? "opacity-60 cursor-not-allowed" : "")}
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              {saving ? "Saving..." : "Save"}
            </PrimaryButton>
          </div>

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                Announcement HTML
              </label>
              <textarea
                value={announcementHtml}
                onChange={(e) => setAnnouncementHtml(e.target.value)}
                className={cx(FieldBase, "min-h-[220px] resize-y font-mono text-[12px]")}
                placeholder={'Example: Limited-time offer: <strong>20% off</strong> with code <strong>SAVE20</strong>'}
                disabled={loading}
              />
              <p className="mt-2 text-xs text-slate-500">
                Empty value keeps storefront fallback text.
              </p>
            </div>

            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                Live Preview
              </label>
              <div className="rounded-2xl border border-slate-200 overflow-hidden">
                <div className="bg-[#D2042D] text-white px-4 py-3 text-sm font-medium">
                  <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </AdminPageShell>
  );
}
